import { DOCUMENT, Injectable, inject } from '@angular/core';
import { EMPTY, Observable, Subject, catchError, filter, merge, of, shareReplay, tap } from 'rxjs';

import { VALID_LANGUAGES } from './constants';
import { DEFAULT_ENGLISH_LANGUAGE_SUPPORT_PROVIDER, LANGUAGE_PROVIDER } from './language-provider.model';
import { Language } from './language.types';

const LOCALSTORAGE_LANG_KEY = 'settings.language';

/**
 * Provides the current language of the user.
 *
 * The initial language is determined in the following order:
 * 1. Language from `LANGUAGE_PROVIDER` if it is a sync value
 * 2. Language from localStorage
 * 3. Language preferred by the user's browser
 * 4. 'de' as fallback
 *
 * Current user language can be customized:
 * @example
 * { provide: LANGUAGE_PROVIDER, useClass: CustomLanguageProvider },
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
    #window = inject(DOCUMENT).defaultView;
    #providedLanguage? = inject(LANGUAGE_PROVIDER, { optional: true })?.getLanguage();
    #supportEnLanguage = inject(DEFAULT_ENGLISH_LANGUAGE_SUPPORT_PROVIDER, { optional: true });

    #localStorage = this.#getLocalStorage();
    #navigator = this.#window?.navigator;
    #languageSubject$ = new Subject<Language>();
    // make sure EN is not an implicit default language - else RWC apps in the field would have a different default language.
    #supportedDefaultLanguages = VALID_LANGUAGES.filter(lang => this.#supportEnLanguage || lang !== 'en');
    #initialLanguage =
        this.#getLanguageFromProviderSync() ??
        this.#getLanguageFromLocalStorage() ??
        this.#getLanguageFromNavigator() ??
        'de';

    readonly language$: Observable<Language> = merge(
        of(this.#initialLanguage),
        this.#getLanguageFromProviderAsync(),
        this.#languageSubject$
    ).pipe(
        filter((lang): lang is Language => !!lang && VALID_LANGUAGES.includes(lang)),
        tap(lang => this.#localStorage?.setItem(LOCALSTORAGE_LANG_KEY, lang)),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    use(lang: Language): void {
        if (!VALID_LANGUAGES.includes(lang)) {
            throw new Error(`Invalid Language ${lang}, use one of ${VALID_LANGUAGES.join(', ')}`);
        }
        this.#languageSubject$.next(lang);
    }

    /**
     * depending on the user settings, the access to the localStorage can throw a SecurityException.
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage#exceptions
     */
    #getLocalStorage(): Storage | undefined {
        try {
            return this.#window?.localStorage;
        } catch (e) {
            console.warn('Error while getting access to window.localStorage. Check your browser settings.', e);
        }
        return undefined;
    }

    #getLanguageFromProviderSync(): Language | undefined {
        return typeof this.#providedLanguage === 'string'
            ? this.#getFirstValidLanguageFrom([this.#providedLanguage])
            : undefined;
    }

    #getLanguageFromProviderAsync(): Observable<Language | undefined> {
        return this.#providedLanguage instanceof Observable
            ? this.#providedLanguage.pipe(catchError(() => EMPTY))
            : EMPTY;
    }

    #getLanguageFromLocalStorage(): Language | undefined {
        const raw = this.#localStorage?.getItem(LOCALSTORAGE_LANG_KEY);
        // if the value was saved with JSON.stringify, it will be wrapped in quotes, so we need to parse it back.
        const value = raw?.startsWith('"') ? JSON.parse(raw) : raw;
        return this.#getFirstValidLanguageFrom([value]);
    }

    #getLanguageFromNavigator(): Language | undefined {
        if (!this.#navigator) {
            return;
        }
        return this.#getFirstValidLanguageFrom([this.#navigator.language, ...(this.#navigator.languages ?? [])]);
    }

    #getFirstValidLanguageFrom(languageOptions: string[]): Language | undefined {
        return languageOptions
            .map(lang => lang?.substring(0, 2).toLowerCase() as Language)
            .find(lang => this.#supportedDefaultLanguages.includes(lang));
    }
}
